/**
 * Parent Value Selector Component
 *
 * Purpose: Select parent value for hierarchical master data values
 * Architecture: Autocomplete with hierarchy path display and validation
 * Dependencies: React, Material-UI, MDM Cache Service, MDM Service (for descendant traversal)
 * Author: Development Team
 * Created: 2025-01-27
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  FolderOpen as FolderIcon,
  Public as GlobalIcon,
  Business as TenantIcon,
  Edit as ModifiedIcon,
} from '@mui/icons-material';
import { MasterDataValue } from '../../types/mdm';
import { useMDMScopedOptions, useMDMValues } from '../../services/mdmCacheService';
import { usePickerScope } from '../../context/PickerScopeContext';
import { mdmService } from '../../services/mdmService'; // Keep for recursive descendant fetching
import { getScopeChipColor } from '../../constants/mdm';

/**
 * Props for ParentValueSelector Component
 */
export interface ParentValueSelectorProps {
  /** Field definition ID (current field) */
  fieldDefId: string;
  /** Field definition with hierarchy_config (to get parent_fielddef_id) */
  fieldDefinition?: { hierarchy_config?: { parent_fielddef_id?: string | null } | null } | null;
  /** Tenant context */
  tenantId: string;
  /** Current value ID (to prevent circular references) */
  currentValueId?: string;
  /** Currently selected parent value ID */
  value?: string | null;
  /** Change handler */
  onChange: (parentValueId: string | null) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Helper text */
  helperText?: string;
  /** Label for the field */
  label?: string;
  /** Required field */
  required?: boolean;
}

/**
 * Option for parent value selection with hierarchy info
 */
interface ParentValueOption {
  value: MasterDataValue;
  hierarchyLabel: string;
  disabled: boolean;
  disabledReason?: string;
}

/**
 * Format hierarchy label for display
 */
const formatHierarchyLabel = (value: MasterDataValue): string => {
  if (value.hierarchy_path && value.hierarchy_path.length > 0) {
    const pathNames = value.hierarchy_path.map((item) => item.name);
    return pathNames.join(' › ');
  }
  return value.name;
};

/**
 * ParentValueSelector Component
 *
 * Provides autocomplete selection of parent values for hierarchical fields.
 * Displays hierarchy path for each option and prevents circular references.
 *
 * Features:
 * - Autocomplete with search
 * - Hierarchy path display in options
 * - Circular reference prevention
 * - Scope badges (GLOBAL/OVERRIDDEN/LOCAL)
 * - Loading and error states
 * - Validation feedback
 *
 * Usage:
 * ```tsx
 * <ParentValueSelector
 *   fieldDefId="fd_country"
 *   tenantId="GLOBAL"
 *   currentValueId={value.id}
 *   value={value.parent_value_id}
 *   onChange={(parentId) => setParentId(parentId)}
 *   label="Parent Country"
 *   required
 * />
 * ```
 */
export const ParentValueSelector: React.FC<ParentValueSelectorProps> = ({
  fieldDefId,
  fieldDefinition,
  tenantId,
  currentValueId,
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  label = 'Parent Value',
  required = false,
}) => {
  const [descendants, setDescendants] = useState<Set<string>>(new Set());
  const [descendantsLoading, setDescendantsLoading] = useState<boolean>(false);

  // Get parent field definition ID from hierarchy_config
  const parentFieldDefId = fieldDefinition?.hierarchy_config?.parent_fielddef_id || null;

  // Fetch parent values using cache service
  // Default filter returns only active, non-deleted values
  const {
    data: parentValues = [],
    isLoading: valuesLoading,
    error: queryError,
  } = useMDMValues(
    parentFieldDefId || '',
    tenantId,
    {}, // Default filter (active values only)
    { enabled: !!parentFieldDefId } // Only fetch if parentFieldDefId exists
  );

  // ORCH-332: a value's parent must lie inside the write's scope (ORCH-21 decision #57),
  // so offer only parents the form's permission reaches. Ancestors kept for navigation,
  // and a current parent outside the scope, show but can't be picked.
  const pickerScope = usePickerScope();
  const {
    scope: loadedScope,
    isLoading: scopeLoading,
    isError: scopeError,
  } = useMDMScopedOptions(parentFieldDefId || '', tenantId, pickerScope, {
    enabled: !!parentFieldDefId,
    offer: true, // ORCH-366: the offer request runs scoped or not
  });
  // A failed scoped fetch offers no new parent (fails closed)
  const scope = scopeError ? new Map<string, boolean>() : loadedScope;

  const loadError = queryError
    ? (queryError as Error).message || 'Failed to load parent values'
    : scopeError
      ? 'Failed to load the parent values you may use'
      : null;
  const loading = valuesLoading || descendantsLoading || scopeLoading;

  /**
   * Fetch all descendants of a value to prevent circular references
   */
  const fetchDescendants = useCallback(
    async (valueId: string): Promise<Set<string>> => {
      const descendants = new Set<string>();

      const findDescendants = async (id: string) => {
        try {
          // Fetch values with this value as parent (use current fieldDefId, not parent)
          const children = await mdmService.getValues({
            fielddef_id: fieldDefId,
            tenant_id: tenantId,
            parent_value_id: id,
          });

          for (const child of children) {
            descendants.add(child.id);
            // Recursively find children's descendants
            await findDescendants(child.id);
          }
        } catch (err) {
          console.error('Error fetching descendants:', err);
        }
      };

      await findDescendants(valueId);
      return descendants;
    },
    [fieldDefId, tenantId]
  );

  // Fetch descendants when currentValueId changes (to prevent circular refs)
  useEffect(() => {
    if (!currentValueId) {
      setDescendants(new Set());
      return;
    }

    const loadDescendants = async () => {
      setDescendantsLoading(true);
      try {
        const descendantIds = await fetchDescendants(currentValueId);
        setDescendants(descendantIds);
      } catch (err) {
        console.error('Failed to load descendants:', err);
      } finally {
        setDescendantsLoading(false);
      }
    };

    loadDescendants();
  }, [currentValueId, fetchDescendants]);

  // Build options with hierarchy labels using useMemo
  const options: ParentValueOption[] = useMemo(() => {
    return parentValues
      .filter((val) => !scope || scope.has(val.id) || val.id === value)
      .map((val) => {
        const isCurrentValue = val.id === currentValueId;
        const isDescendant = descendants.has(val.id);
        const isSoftDeleted = val.is_deleted;
        const outOfScope = !!scope && scope.get(val.id) !== true;

        let disabledReason: string | undefined;
        if (isCurrentValue) {
          disabledReason = 'Cannot select self as parent';
        } else if (isDescendant) {
          disabledReason = 'Would create circular reference';
        } else if (isSoftDeleted) {
          disabledReason = 'Value is deleted';
        } else if (outOfScope) {
          disabledReason = 'Outside your master-data scope';
        }

        return {
          value: val,
          hierarchyLabel: formatHierarchyLabel(val),
          disabled: isCurrentValue || isDescendant || isSoftDeleted || outOfScope,
          disabledReason,
        };
      });
  }, [parentValues, currentValueId, descendants, scope, value]);

  /**
   * Get scope icon
   */
  const getScopeIcon = (scope?: string) => {
    switch (scope) {
      case 'GLOBAL':
        return <GlobalIcon sx={{ fontSize: 14 }} />;
      case 'OVERRIDDEN':
        return <ModifiedIcon sx={{ fontSize: 14 }} />;
      case 'LOCAL':
        return <TenantIcon sx={{ fontSize: 14 }} />;
      default:
        return null;
    }
  };

  /**
   * Get scope label
   */
  const getScopeLabel = (scope?: string) => {
    switch (scope) {
      case 'OVERRIDDEN':
        return 'OVERRIDDEN';
      case 'LOCAL':
        return 'LOCAL';
      case 'GLOBAL':
        return 'GLOBAL';
      default:
        return null;
    }
  };

  /**
   * Get selected value object
   */
  const selectedValue = options.find((opt) => opt.value.id === value)?.value || null;

  /**
   * Handle selection change
   */
  const handleChange = (_event: any, newValue: MasterDataValue | null) => {
    onChange(newValue?.id || null);
  };

  // Error state
  if (loadError) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        {loadError}
      </Alert>
    );
  }

  return (
    <Autocomplete
      value={selectedValue}
      onChange={handleChange}
      options={options.map((opt) => opt.value)}
      getOptionLabel={(option) => option.name}
      getOptionDisabled={(option) => {
        const opt = options.find((o) => o.value.id === option.id);
        return opt?.disabled || false;
      }}
      loading={loading}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          fullWidth
          margin="normal"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <FolderIcon sx={{ mr: 1, color: 'action.active' }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const opt = options.find((o) => o.value.id === option.id);
        const scopeLabel = getScopeLabel(option.scope_info?.scope);
        const scopeIcon = getScopeIcon(option.scope_info?.scope);

        return (
          <Box component="li" {...props} key={option.id}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              {/* Main label with hierarchy path */}
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="body2">{option.name}</Typography>

                {/* Scope badge */}
                {scopeLabel && (
                  <Chip
                    label={scopeLabel}
                    size="small"
                    {...(scopeIcon && { icon: scopeIcon })}
                    color={getScopeChipColor(option.scope_info?.scope)}
                    sx={{
                      height: 18,
                      fontSize: '0.7rem',
                    }}
                  />
                )}
              </Box>

              {/* Hierarchy path (if different from name) */}
              {opt && opt.hierarchyLabel !== option.name && (
                <Typography variant="caption" color="text.secondary">
                  {opt.hierarchyLabel}
                </Typography>
              )}

              {/* Disabled reason */}
              {opt?.disabled && opt.disabledReason && (
                <Typography variant="caption" color="error" fontStyle="italic">
                  {opt.disabledReason}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      noOptionsText={loading ? 'Loading...' : 'No parent values available'}
      fullWidth
    />
  );
};

export default ParentValueSelector;
